import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", contact: "", email: "", phone: "", address: "" };

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/suppliers");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, contact: r.contact || "", email: r.email || "", phone: r.phone || "", address: r.address || "" }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
        toast.success("Fornitore aggiornato");
      } else {
        await api.post("/suppliers", form);
        toast.success("Fornitore creato");
      }
      setShowForm(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare fornitore "${r.name}"?`)) return;
    try {
      await api.delete(`/suppliers/${r.id}`);
      toast.success("Fornitore eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const field = (key, label, type = "text") => (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <input type={type} data-testid={`supplier-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
    </label>
  );

  return (
    <div>
      <PageHeader
        title="Fornitori"
        subtitle="Gestisci l'anagrafica fornitori"
        action={
          <button data-testid="new-supplier-btn" onClick={openNew} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2">
            <Plus size={16} weight="bold" /> Nuovo Fornitore
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Nome</th>
                <th className="px-4 py-3 label-eyebrow">Contatto</th>
                <th className="px-4 py-3 label-eyebrow">Email</th>
                <th className="px-4 py-3 label-eyebrow">Telefono</th>
                <th className="px-4 py-3 label-eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-neutral-500">Nessun fornitore.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.contact || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.email || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600 font-mono">{r.phone || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-supplier-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-supplier-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-red-100 text-red-600 transition-colors"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-y-auto" data-testid="supplier-form">
            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuovo"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Fornitore</h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">{field("name", "Nome *")}</div>
              {field("contact", "Referente")}
              {field("email", "Email", "email")}
              {field("phone", "Telefono")}
              <div className="md:col-span-2">{field("address", "Indirizzo")}</div>
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="supplier-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
